export const MAX_IMPORT_ROWS = 1000;
export const MAX_IMPORT_FILE_BYTES = 5 * 1024 * 1024;

export const JOB_IMPORT_FIELDS = [
  { key: 'jobNumber', label: 'Job Number', required: false },
  { key: 'jobName', label: 'Job Name', required: true },
  { key: 'customerName', label: 'Customer Name', required: true },
  { key: 'poNumber', label: 'PO Number', required: false },
  { key: 'estimatedValue', label: 'Estimated Value', required: false },
  { key: 'dueDate', label: 'Due Date', required: false },
  { key: 'stage', label: 'Stage', required: false },
];

const blankMapping = () => Object.fromEntries(JOB_IMPORT_FIELDS.map(({ key }) => [key, '']));
const clean = (value) => String(value ?? '').trim();
const normalize = (value) => clean(value).toLocaleLowerCase('en-US').replace(/\s+/g, ' ');

export function parseCSV(text) {
  const source = String(text ?? '').replace(/^\uFEFF/, '');
  const records = [];
  let record = [];
  let cell = '';
  let quoted = false;

  for (let index = 0; index < source.length; index += 1) {
    const char = source[index];
    if (char === '"') {
      if (quoted && source[index + 1] === '"') {
        cell += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (char === ',' && !quoted) {
      record.push(cell.trim());
      cell = '';
    } else if ((char === '\n' || char === '\r') && !quoted) {
      if (char === '\r' && source[index + 1] === '\n') index += 1;
      record.push(cell.trim());
      if (record.some((value) => value !== '')) records.push(record);
      record = [];
      cell = '';
    } else {
      cell += char;
    }
  }

  if (quoted) throw new Error('The CSV contains an unclosed quoted field.');
  record.push(cell.trim());
  if (record.some((value) => value !== '')) records.push(record);
  if (records.length < 2) throw new Error('The CSV needs a header row and at least one job.');

  const headers = records[0].map(clean);
  if (headers.some((header) => !header)) throw new Error('Every CSV column needs a header.');
  const normalizedHeaders = headers.map(normalize);
  if (new Set(normalizedHeaders).size !== headers.length) {
    throw new Error('CSV column headers must be unique.');
  }

  const rows = records.slice(1).map((row) => headers.map((_, index) => row[index] ?? ''));
  if (rows.length > MAX_IMPORT_ROWS) {
    throw new Error(`Import up to ${MAX_IMPORT_ROWS.toLocaleString()} jobs at a time.`);
  }
  return { headers, rows };
}

export function autoMapHeaders(headers) {
  const mapping = blankMapping();
  headers.forEach((header) => {
    const key = normalize(header).replace(/[^a-z0-9]/g, '');
    if (!mapping.jobNumber && (key === 'jobnumber' || key === 'jobno' || key === 'job')) mapping.jobNumber = header;
    if (!mapping.jobName && ['jobname', 'jobdescription', 'description', 'projectname'].includes(key)) mapping.jobName = header;
    if (!mapping.customerName && (key.includes('customer') || key.includes('client'))) mapping.customerName = header;
    if (!mapping.poNumber && ['ponumber', 'pono', 'purchaseorder', 'customerpo'].includes(key)) mapping.poNumber = header;
    if (!mapping.estimatedValue && ['estimatedvalue', 'value', 'amount', 'price', 'jobvalue'].includes(key)) mapping.estimatedValue = header;
    if (!mapping.dueDate && ['duedate', 'deliverydate', 'requiredby', 'promiseddate'].includes(key)) mapping.dueDate = header;
    if (!mapping.stage && ['stage', 'status', 'workflowstage'].includes(key)) mapping.stage = header;
  });
  return mapping;
}

function parseAmount(raw) {
  const value = clean(raw);
  if (!value) return { value: null };
  const normalizedValue = value.replace(/[$,\s]/g, '');
  if (!/^-?\d+(\.\d{1,2})?$/.test(normalizedValue)) return { error: 'Estimated value is not a valid amount.' };
  const number = Number(normalizedValue);
  if (!Number.isFinite(number) || number < 0 || number > 999999999999) {
    return { error: 'Estimated value must be between $0 and $999,999,999,999.' };
  }
  return { value: number };
}

function parseDate(raw) {
  const value = clean(raw);
  if (!value) return { value: null };
  let year;
  let month;
  let day;
  let match = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (match) [, year, month, day] = match;
  else {
    match = value.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (match) [, month, day, year] = match;
  }
  if (!match) return { error: 'Due date must use YYYY-MM-DD or MM/DD/YYYY.' };
  const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const parsed = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== iso) {
    return { error: 'Due date is not a real calendar date.' };
  }
  return { value: iso };
}

function duplicateKey(row) {
  if (row.jobNumber) return `number:${normalize(row.jobNumber)}`;
  return `job:${normalize(row.jobName)}|${normalize(row.customerName)}|${normalize(row.poNumber)}`;
}

export function buildImportReview({ headers, rows, mapping, stages = [], existingJobs = [] }) {
  const indexes = Object.fromEntries(Object.entries(mapping).map(([field, header]) => [field, headers.indexOf(header)]));
  const stageMap = new Map(stages.map((stage) => [normalize(stage.name), stage]));
  const existingKeys = new Set(existingJobs.flatMap((job) => {
    const keys = [];
    if (job.job_number) keys.push(`number:${normalize(job.job_number)}`);
    keys.push(`job:${normalize(job.name)}|${normalize(job.customers?.name)}|${normalize(job.po_number)}`);
    return keys;
  }));
  const fileKeys = new Set();

  const reviewedRows = rows.map((sourceRow, index) => {
    const get = (field) => indexes[field] >= 0 ? clean(sourceRow[indexes[field]]) : '';
    const amount = parseAmount(get('estimatedValue'));
    const date = parseDate(get('dueDate'));
    const requestedStage = get('stage');
    const matchedStage = requestedStage ? stageMap.get(normalize(requestedStage)) : stages[0];
    const row = {
      rowNumber: index + 2,
      jobNumber: get('jobNumber'),
      jobName: get('jobName'),
      customerName: get('customerName'),
      poNumber: get('poNumber'),
      estimatedValue: amount.value ?? null,
      dueDate: date.value ?? null,
      stageName: matchedStage?.name ?? '',
      stageId: matchedStage?.id ?? null,
      issues: [],
    };

    if (!row.jobName) row.issues.push('Job name is required.');
    if (!row.customerName) row.issues.push('Customer name is required.');
    if (row.jobNumber.length > 80) row.issues.push('Job number is longer than 80 characters.');
    if (row.jobName.length > 240) row.issues.push('Job name is longer than 240 characters.');
    if (row.customerName.length > 240) row.issues.push('Customer name is longer than 240 characters.');
    if (row.poNumber.length > 120) row.issues.push('PO number is longer than 120 characters.');
    if (amount.error) row.issues.push(amount.error);
    if (date.error) row.issues.push(date.error);
    if (requestedStage && !matchedStage) row.issues.push(`Stage “${requestedStage}” does not exist in this shop.`);
    if (!matchedStage) row.issues.push('The shop needs at least one workflow stage before jobs can be imported.');

    row.sourceKey = duplicateKey(row);
    if (existingKeys.has(row.sourceKey)) row.duplicateReason = 'Already exists in TrackShop';
    else if (fileKeys.has(row.sourceKey)) row.duplicateReason = 'Repeated in this CSV';
    fileKeys.add(row.sourceKey);
    row.status = row.issues.length ? 'error' : row.duplicateReason ? 'duplicate' : 'ready';
    return row;
  });

  return {
    rows: reviewedRows,
    readyRows: reviewedRows.filter((row) => row.status === 'ready'),
    duplicateRows: reviewedRows.filter((row) => row.status === 'duplicate'),
    errorRows: reviewedRows.filter((row) => row.status === 'error'),
  };
}

export function importPayload(review) {
  return review.readyRows.map((row) => ({
    row_number: row.rowNumber,
    source_key: row.sourceKey,
    job_number: row.jobNumber || null,
    job_name: row.jobName,
    customer_name: row.customerName,
    po_number: row.poNumber || null,
    estimated_value: row.estimatedValue,
    due_date: row.dueDate,
    stage_id: row.stageId,
  }));
}
